/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import FormTextField from "../../../components/FormTextField/FormTextField";
import {
	TextField,
	Tooltip,
	InputAdornment,
	MenuItem,
	Accordion,
	AccordionSummary,
	Typography,
	Checkbox,
	CardContent,
	Grid,
} from "@material-ui/core";
import { CALL_ME_NOW_TOOL_TIPS } from "../../../utility/toolTipMessages";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CallSubjectList from "../components/CallSubjectList";
import { v4 as uuid } from "uuid";
import { ActionButtons } from "../../../components/Common";
const useStyles = makeStyles(() => ({
	accordion: {
		background: "#eeeeee",
	},
}));
const CallMeNowConfig = ({
	selectedConfiguration,
	contactFlows,
	setSelectedConfiguration,
	setFormEdited,
	formEdited,
	phoneNumbers,
}) => {
	const classes = useStyles();
	const [expanded, setExpanded] = React.useState(["amazonConnectSettings"]);

	const isPanelExpanded = (panel) => {
		return expanded.find((e) => e === panel) === panel ? true : false;
	};
	React.useEffect(() => {
		if (!formEdited && selectedConfiguration) {
			setExpanded(["amazonConnectSettings"]);
		}
	}, [selectedConfiguration, formEdited]);

	const onExpandAllClick = () => {
		setExpanded(["amazonConnectSettings", "widget", "callResons", "toNumber", "callButton"]);
	};
	const onCollapseAllClick = () => {
		setExpanded([]);
	};
	const handleChange = (panel) => (event, newExpanded) => {
		if (newExpanded) {
			setExpanded(expanded.concat(panel));
		} else {
			const newArray = expanded.filter((e) => e !== panel);
			setExpanded(newArray);
		}
	};
	const setConfigValue = (valueName, value) => {
		setFormEdited(true);
		let updateCallMeNowConfig = { ...selectedConfiguration };
		updateCallMeNowConfig[valueName] = value;
		setSelectedConfiguration(updateCallMeNowConfig);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const updateSubject = React.useCallback((subjects) => {
		setFormEdited(true);
		let updateCallMeNowConfig = { ...selectedConfiguration };
		updateCallMeNowConfig.subjects = subjects;
		setSelectedConfiguration(updateCallMeNowConfig);
	});

	const addNewSubject = () => {
		setFormEdited(true);
		let updateCallMeNowConfig = { ...selectedConfiguration };
		updateCallMeNowConfig.subjects = [
			{
				subjectId: uuid(),
				subject: "",
			},
			...updateCallMeNowConfig.subjects,
		];
		setSelectedConfiguration(updateCallMeNowConfig);
	};
	return (
		<CardContent>
			<ActionButtons
				className="justify-content-end"
				showCancel
				showRemove
				cancelBtnClass="text-dark border-secondary"
				removeBtnClass="text-dark border-secondary"
				onCancel={onExpandAllClick}
				onRemove={onCollapseAllClick}
				cancelLabel="Expand All"
				removeLabel="Collapse All"
			/>
			<div style={{ marginTop: "10px" }}>
				<Accordion expanded={isPanelExpanded("amazonConnectSettings")} onChange={handleChange("amazonConnectSettings")}>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="amazonConnectSettings"
						id="amazonConnectSettings"
					>
						Amazon Connect
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<TextField
								label={"Instance ID"}
								defaultValue={selectedConfiguration.instanceId}
								disabled
								className="w-100"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Tooltip arrow interactive placement={"bottom-start"} title={CALL_ME_NOW_TOOL_TIPS.INSTANCEID}>
												<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
											</Tooltip>
										</InputAdornment>
									),
								}}
							/>
						</Grid>
						{contactFlows && (
							<Grid item xs={12} lg={6}>
								<TextField
									label={"Contact Flow"}
									select
									className="w-100"
									value={selectedConfiguration.contactFlowId || ""}
									error={!selectedConfiguration.contactFlowId || selectedConfiguration.contactFlowId.length === 0}
									onChange={(event) => {
										const edited = selectedConfiguration.contactFlowId !== event.target.value;
										if (edited) setConfigValue("contactFlowId", event.target.value);
									}}
									helperText={
										(!selectedConfiguration.contactFlowId || selectedConfiguration.contactFlowId.length === 0) &&
										"A Contact Flow is required"
									}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<Tooltip
													arrow
													interactive
													placement={"bottom-start"}
													title={CALL_ME_NOW_TOOL_TIPS.CONTACT_FLOW}
												>
													<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
												</Tooltip>
											</InputAdornment>
										),
									}}
								>
									{(contactFlows || []).map((flow) => {
										return (
											<MenuItem key={flow.Id} value={flow.Id}>
												{flow.Name}
											</MenuItem>
										);
									})}
								</TextField>
							</Grid>
						)}
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Allowed Origins"}
								formEdited={formEdited}
								required
								errorMessage={"Must be at least one allowed origin."}
								styleObject={{ width: "100%" }}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.allowedOrigins || ""}
								setConfigValue={setConfigValue}
								valueName={"allowedOrigins"}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.ALLOWED_ORIGINS}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Configuration Friendly Name"}
								formEdited={formEdited}
								required
								styleObject={{ width: "100%" }}
								errorMessage={"Configuration friendly name cannot be empty"}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.configName || ""}
								setConfigValue={setConfigValue}
								valueName={"configName"}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.CONFIG_NAME}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							{phoneNumbers && (
								<TextField
									label={"Source Phone Number"}
									select
									className="w-100"
									value={selectedConfiguration.sourcePhoneNumber || ""}
									error={
										!selectedConfiguration.sourcePhoneNumber || selectedConfiguration.sourcePhoneNumber.length === 0
									}
									onChange={(event) => {
										const edited = selectedConfiguration.sourcePhoneNumber !== event.target.value;
										if (edited) setConfigValue("sourcePhoneNumber", event.target.value);
									}}
									helperText={
										(!selectedConfiguration.sourcePhoneNumber ||
											selectedConfiguration.sourcePhoneNumber.length === 0) &&
										"A source phone number is required"
									}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<Tooltip
													arrow
													interactive
													placement={"bottom-start"}
													title={CALL_ME_NOW_TOOL_TIPS.SOURCE_PHONE_NUMBER}
												>
													<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
												</Tooltip>
											</InputAdornment>
										),
									}}
								>
									{(phoneNumbers || []).map((phone) => {
										return (
											<MenuItem key={phone.Id} value={phone.PhoneNumber}>
												{phone.PhoneNumber}
											</MenuItem>
										);
									})}
								</TextField>
							)}
						</Grid>
					</Grid>
				</Accordion>
				<Accordion style={{ marginTop: "10px" }} expanded={isPanelExpanded("widget")} onChange={handleChange("widget")}>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="widget"
						id="widget"
					>
						Widget
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Title Text"}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.TITLE_TEXT}
								formEdited={formEdited}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.titleText || ""}
								setConfigValue={setConfigValue}
								valueName={"titleText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Title Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.TITLE_TEXT_COLOR}
								startingValue={selectedConfiguration.titleTextColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"titleTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Header Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.HEADER_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.headerBackgroundColor || "#CE2727"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"headerBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Header Background Image"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								placeholder="Image URL"
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.HEADER_BACKGROUND_IMAGE}
								startingValue={selectedConfiguration.headerBackgroundImage}
								type={"text"}
								setConfigValue={setConfigValue}
								valueName={"headerBackgroundImage"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Widget Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.WIDGET_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.widgetBackgroundColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"widgetBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Widget Background Image"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								placeholder="Image URL"
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.WIDGET_BACKGROUND_IMAGE}
								startingValue={selectedConfiguration.widgetBackgroundImage}
								type={"text"}
								setConfigValue={setConfigValue}
								valueName={"widgetBackgroundImage"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Call Success Message"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.CALL_SUCCESS_MESSAGE}
								startingValue={selectedConfiguration.callSuccessMessage}
								type={"text"}
								setConfigValue={setConfigValue}
								valueName={"callSuccessMessage"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Call Failure Message"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.CALL_FAILURE_MESSAGE}
								startingValue={selectedConfiguration.callFailureMessage}
								type={"text"}
								setConfigValue={setConfigValue}
								valueName={"callFailureMessage"}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("callResons")}
					onChange={handleChange("callResons")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="callResons"
						id="callResons"
					>
						Calling Options
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<div className="w-100">
								<Typography style={{ fontSize: 12, marginLeft: "10px", color: "gray" }}>Enable Call Options</Typography>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={CALL_ME_NOW_TOOL_TIPS.ENABLED_CALL_OPTIONS}
								>
									<i
										style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }}
										className="fas fa-info-circle"
									/>
								</Tooltip>
								<Checkbox
									checked={selectedConfiguration.enabledCallOptions || false}
									onChange={(event) => {
										setConfigValue("enabledCallOptions", event.target.checked);
									}}
								/>
							</div>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Label Text"}
								error={!selectedConfiguration.enabledCallOptions && !selectedConfiguration.callOptionsLabelText}
								helperText={
									!selectedConfiguration.enabledCallOptions &&
									!selectedConfiguration.callOptionsLabelText &&
									"Options label cannot be empty."
								}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.OPTIONS_LABEL_TEXT}
								formEdited={formEdited}
								setEdited={setFormEdited}
								disabled={!selectedConfiguration.enabledCallOptions}
								startingValue={selectedConfiguration.callOptionsLabelText || ""}
								setConfigValue={setConfigValue}
								valueName={"callOptionsLabelText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Label Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								disabled={!selectedConfiguration.enabledCallOptions}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.OPTIONS_LABEL_TEXT_COLOR}
								startingValue={selectedConfiguration.callOptionsLabelTextColor || "#6a6666"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"callOptionsLabelTextColor"}
							/>
						</Grid>
						{selectedConfiguration.enabledCallOptions && (
							<Grid item xs={12}>
								<CallSubjectList
									subjectsList={selectedConfiguration.subjects}
									updateSubjectHandler={updateSubject}
									addNewSubjectHandler={addNewSubject}
								/>
							</Grid>
						)}
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("toNumber")}
					onChange={handleChange("toNumber")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="toNumber"
						id="toNumber"
					>
						To Number
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Label Text"}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.TO_NUMBER_LABEL_TEXT}
								formEdited={formEdited}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.toNumberLabelText || ""}
								setConfigValue={setConfigValue}
								valueName={"toNumberLabelText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Label Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.TO_NUMBER_LABEL_TEXT_COLOR}
								startingValue={selectedConfiguration.toNumberLabelTextColor || "#6a6666"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"toNumberLabelTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Validation Message"}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.TO_NUMBER_VALIDATION_MESSAGE}
								formEdited={formEdited}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.toNumberValidationMessage || ""}
								setConfigValue={setConfigValue}
								valueName={"toNumberValidationMessage"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Validation Message Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.VALIDATION_MESSAGE_TEXT_COLOR}
								startingValue={selectedConfiguration.validationMessageTextColor || "#f50000"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"validationMessageTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Placeholder Text"}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.TO_NUMBER_PLACEHOLDER_TEXT}
								formEdited={formEdited}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.toNumberPlaceholderText || "+14791112345"}
								setConfigValue={setConfigValue}
								valueName={"toNumberPlaceholderText"}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("callButton")}
					onChange={handleChange("callButton")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="callButton"
						id="callButton"
					>
						Call Button
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Button Text"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.CALL_BUTTON_TEXT}
								startingValue={selectedConfiguration.callButtonText || ""}
								setConfigValue={setConfigValue}
								valueName={"callButtonText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Button Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.CALL_BUTTON_TEXT_COLOR}
								startingValue={selectedConfiguration.buttonTextColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"buttonTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Button Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.CALL_BUTTON_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.buttonBackgroundColor || "#941012"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"buttonBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Disabled Button Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.CALL_BUTTON_DISABLED_TEXT_COLOR}
								startingValue={selectedConfiguration.disabledButtonTextColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"disabledButtonTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Disabled Button Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={CALL_ME_NOW_TOOL_TIPS.CALL_BUTTON_DISABLED_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.disabledButtonBackgroundColor || "#6a6666"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"disabledButtonBackgroundColor"}
							/>
						</Grid>
					</Grid>
				</Accordion>
			</div>
		</CardContent>
	);
};

export default CallMeNowConfig;
